// @flow
import React, { type ComponentType, PureComponent } from "react";
import { connect } from "react-redux";
import glamorous from "glamorous";
import cx from "classnames";
import styled from "styled-components";
import { Tooltip } from "react-tippy";
import { isMobile } from "utils/domUtils";
import DropdownList from "root/components/DropdownList/DropdownList";
import { selectPalette } from "root/selectors";
import SvgIcon from "../SvgIcon";
import SVG_ICONS from "~/assets/icons/icons";

import "./SearchBox.scss";

export const SEARCH_BOX_MODES = {
	BUTTON: 1,
	EXPANDED: 2,
};

type OwnProps = {|
	mode?: number,
	inputPlaceholder: string,
	children?: any,
	termChangeHandler?: ?Function,
	searchHandler: Function,
	searchOptions?: Array<string>, /* if defined will add a drop menu on the left side of the search field */
	selectedSearchOption?: ?string,
	searchOptionHandler?: Function,
	showClearButton?: boolean,
	onClearClickHandler?: Function,
	onEscClickHandler?: Function,
	clearBtnTip?: string,
	value: ?string,
|};

type Props = {
	...OwnProps,
	palette: Object,
};

const Wrapper: ComponentType<{ mode: ?number, palette: Object }> = styled.div.attrs((props) => ({
	className: `search-bar search-form search-box bc-window-border active search-box-mode-${props.mode}`,
}))`
    border-width: 1px;
    border-style: solid;
    background-color: #ffffff;

    &:focus-within {
        border-color: ${(props) => props.palette.link};

        .change-fill-on-focus path {
            fill: ${(props) => props.palette.link};
        }
    }
`;

export class SearchBox extends PureComponent<Props> {
	static defaultProps = {
		inputPlaceholder: "",
		searchHandler: null,
		mode: SEARCH_BOX_MODES.EXPANDED,
		value: "",
	};

	componentDidMount() {
		this.setFocusOnSearchInput();
	}

	componentDidUpdate(prevProps: Props) {
		if (prevProps.mode !== this.props.mode) {
			this.setFocusOnSearchInput();
		}
		if (
			this.searchInput
			&& prevProps.value?.length
			&& !this.props.value?.length
		) {
			this.searchInput.value = "";
		}
	}

	onSearchClick = () => {
		if (this.props.searchHandler) {
			this.props.searchHandler();
			if (isMobile() && this.searchInput) {
				this.searchInput.blur();
			}
		}
	};

	onSearchInputChange = () => {
		if (this.props.termChangeHandler && this.searchInput) {
			this.props.termChangeHandler(this.searchInput.value);
		}
	};

	onSearchInputKeyUp = (e: KeyboardEvent) => {
		if (e.which === 13 && this.props.searchHandler) {
			this.props.searchHandler();
			if (isMobile() && this.searchInput) {
				this.searchInput.blur();
			}
		}

		if (e.which === 27 && this.props.onEscClickHandler) {
			this.props.onEscClickHandler();
			e.stopPropagation();
		}
	};

	setFocusOnSearchInput = () => {
		if (this.props.mode === SEARCH_BOX_MODES.EXPANDED) {
			if (this.searchInput) {
				this.searchInput.focus();
			}
		}
	};

	searchInput: ?HTMLInputElement;

	render() {
		const { props } = this;

		const isExpanded = props.mode === SEARCH_BOX_MODES.EXPANDED;
		const mode = isExpanded
			? SEARCH_BOX_MODES.EXPANDED
			: SEARCH_BOX_MODES.BUTTON;

		return isExpanded ? (
			<Wrapper mode={mode} palette={props.palette}>
				<div className="form">
					{props.searchOptions && (
						<DropdownList
							className="search-types"
							dataTest="search-types"
							items={props.searchOptions}
							selectedItem={props.selectedSearchOption}
							onChange={(value) => {
								if (props.searchOptionHandler) {
									props.searchOptionHandler(value);
								}
								if (this.searchInput) {
									this.searchInput.focus();
								}
							}}
						/>
					)}
					<input
						type="search"
						data-test="search-input"
						className={cx("text-field search-input outline-0", {
							"with-clear-btn": props.showClearButton,
						})}
						placeholder={props.inputPlaceholder}
						onChange={this.onSearchInputChange}
						onKeyUp={this.onSearchInputKeyUp}
						style={{
							height: "20px",
							color: "initial",
						}} /* TODO: Temporary! */
						ref={(elm) => (this.searchInput = elm)}
						value={props.value}
					/>

					<glamorous.Div
						data-test="do-clear"
						className={cx(
							"pointer absolute top-0 h-100 flex items-center justify-center clear-btn",
							{ show: props.showClearButton },
						)}
						right={28}
						top={7}
					>
						{props.showClearButton ? (
							<Tooltip
								title={props.clearBtnTip}
								arrow
								animation="scale"
								delay={250}
								duration={200}
								hideDelay={0}
								size="small"
								className="tip"
							>
								<SvgIcon
									path={SVG_ICONS.CANCEL}
									size="xl"
									colorName="window-border"
									className="change-fill-on-focus"
									onClick={props.onClearClickHandler}
								/>
							</Tooltip>
						) : null}
					</glamorous.Div>

					<glamorous.Div
						data-test="do-search"
						className="do-search pointer absolute top-0 h-100 flex items-center justify-center"
					>
						<SvgIcon
							path={SVG_ICONS.SEARCH}
							size="sm"
							colorName="window-border"
							className="change-fill-on-focus"
							onClick={this.onSearchClick}
						/>
					</glamorous.Div>
				</div>

				{props.children}
			</Wrapper>
		) : (
			<glamorous.Div
				data-test="do-search"
				className="do-search pointer h-100 flex items-center justify-center"
			>
				<SvgIcon
					path={SVG_ICONS.SEARCH}
					size="sm"
					colorName="link"
					onClick={this.onSearchClick}
				/>
			</glamorous.Div>
		);
	}
}

const mapStateToProps = (state) => ({
	palette: selectPalette(state),
});

export default connect<Props, OwnProps, _, _, _, _>(mapStateToProps)(SearchBox);
